import type { CardVariant } from '~/components/ui/card'
import { cn, formatPercentage } from '~/lib/utils'
import { Badge } from '../../ui/badge'
import { Card, CardContent } from '../../ui/card'
import { AppAreaChart, type AppAreaChartProps } from './charts/app-area-chart'
import { AppBarChart, type AppBarChartProps } from './charts/app-bar-chart'
import { AppPieChart, type AppPieChartProps } from './charts/app-pie-chart'

const widthPxForCardWidth = (w?: 'default' | 'md' | 'lg') => {
  if (w === 'md') return 175
  // default and lg both behave like 250px in our UI (due to cardWidth and/or Card variant min-width)
  return 250
}

interface StatCardProps<TChartXAxisKey extends string, TChartYAxisKey extends string> {
  title: string
  size?: 'default' | 'sm' | 'md' | 'lg' | 'huge'
  variant?: CardVariant
  unitSize?: 'text-xl'
  titleSize?: 'text-xs'
  valueSize?: 'default' | 'huge'
  cardWidth?: 'default' | 'md' | 'lg'
  className?: string
  value: {
    amount: number
    unit?: '€' | 'k€' | 'h' | 'j' | 'mo' | 'a' | 'k/an' | '%' | 'm²' | (string & {})
    suffix?: string
    percentage?: number
    index?: string
  }
  chart?:
    | ({ type: 'bar' } & AppBarChartProps<TChartXAxisKey, TChartYAxisKey>)
    | ({ type: 'area' } & AppAreaChartProps<TChartXAxisKey, TChartYAxisKey>)
    | ({ type: 'pie' } & AppPieChartProps)
  leftInnerStatCard?: StatCardProps<TChartXAxisKey, TChartYAxisKey>
  rightInnerStatCard?: StatCardProps<TChartXAxisKey, TChartYAxisKey>
}

export const StatCard = <TChartXAxisKey extends string, TChartYAxisKey extends string>({
  title,
  value,
  chart,
  size = 'default',
  titleSize = 'text-xs',
  unitSize = 'text-xl',
  valueSize = 'default',
  cardWidth = 'default',
  variant,
  className,
  leftInnerStatCard,
  rightInnerStatCard,
}: StatCardProps<TChartXAxisKey, TChartYAxisKey>) => {
  const hasInnerCards = Boolean(leftInnerStatCard && rightInnerStatCard)

  // When nesting cards, force the outer card width to match the combined width
  // of the two inner cards (plus the gap), so the background doesn't become wider.
  const combinedInnerWidthPx = hasInnerCards
    ? widthPxForCardWidth(leftInnerStatCard?.cardWidth) +
      widthPxForCardWidth(rightInnerStatCard?.cardWidth) +
      8 // gap-2 = 8px
    : undefined

  return (
    <Card
      variant={variant}
      style={combinedInnerWidthPx ? { width: combinedInnerWidthPx } : undefined}
      className={cn(
        // Compact style only for small cards (map hover, etc.).
        // Other sizes keep the default spacing from `Card`.
        size === 'sm' && 'w-full p-3 gap-2 rounded-xl min-w-0',
        !hasInnerCards && cardWidth === 'md' && 'w-[175px]',
        !hasInnerCards && cardWidth === 'lg' && 'w-[250px]',
        hasInnerCards && 'min-w-0',
        className,
      )}
    >
      <CardContent
        className={cn(
          'h-full flex flex-col',
          size === 'sm' && 'gap-1',
          size === 'md' && 'gap-2.5',
          size === 'lg' && 'gap-3',
          size === 'huge' && 'gap-3.5',
        )}
      >
        <h3
          className={cn(
            'uppercase font-["Avenir"]',
            size === 'sm' ? 'text-[10px] font-semibold tracking-wide' : titleSize,
            size === 'huge' && 'text-sm font-semibold',
            variant === 'gradient' ? 'text-[12px] font-extrabold leading-4 text-foreground' : '',
            // Keep huge cards stable when the title wraps: reserve 2 lines and clip overflow.
            // Use a tighter line-height so the huge card doesn't get taller.
            size === 'huge' && 'leading-4 min-h-8 max-h-8 overflow-hidden',
          )}
        >
          {title}
        </h3>

        {!hasInnerCards && (
          <div
            className={cn(
              'flex items-end',
              size === 'sm' && 'gap-1.5 text-2xl leading-none',
              size !== 'sm' && 'gap-4',
              // Keep the same overall card height for huge cards, even when the value isn't huge.
              // This reserves the vertical space used by the 76px value (line-height: 66px).
              // Use 72px so it matches the nested-inner-cards layout height as well.
              size === 'huge' && 'min-h-[72px]',
              valueSize === 'huge' && 'text-6xl leading-none',
              size !== 'sm' && valueSize !== 'huge' && 'text-4xl',
            )}
          >
            <span
              className={cn(
                "font-bold font-['Avenir Next Condensed']",
                variant === 'gradient' && 'text-foreground leading-8',
                valueSize === 'huge' && 'text-white text-[76px] leading-[66px]',
              )}
            >
              {new Intl.NumberFormat('fr-FR').format(value.amount)}
              <span
                className={cn(
                  unitSize,
                  variant === 'gradient' &&
                    "text-foreground font-['Avenir Next Condensed'] font-bold leading-8",
                )}
              >
                {value.unit === '€' ||
                value.unit === 'k/an' ||
                value.unit === '%' ||
                value.unit === 'm²'
                  ? ' '
                  : ''}
                {value.unit}
              </span>
            </span>

            {value.index && (
              <span
                className={cn(
                  variant === 'gradient' &&
                    'text-foreground text-[10px] not-italic font-extrabold leading-4 font-[Avenir]',
                  variant === 'gradient-strong' &&
                    'text-white font-["Avenir"] text-sm font-extrabold leading-4',
                  size === 'sm' ? 'mb-0' : 'mb-0.5',
                  size === 'huge' && 'mb-1',
                )}
              >
                {value.index}
              </span>
            )}

            {value.suffix && (
              <span className={cn(size === 'sm' ? 'text-sm mb-0' : 'text-xl mb-0.5')}>
                {value.suffix}
              </span>
            )}

            {value.percentage && (
              <Badge
                variant="success"
                className={cn(
                  size === 'sm'
                    ? 'mb-0.5 font-semibold text-[10px] px-1.5 py-0.5'
                    : 'mb-1.5 font-semibold',
                )}
              >
                {formatPercentage(value.percentage)}
              </Badge>
            )}
          </div>
        )}

        {hasInnerCards && (
          <div
            className={cn(
              'flex gap-2',
              // Align huge cards with and without inner cards.
              // The inner cards are ~72px tall; we reserve the same space here.
              size === 'huge' ? 'min-h-[72px] items-center' : 'items-center',
            )}
          >
            <StatCard {...(leftInnerStatCard as any)} />
            <StatCard {...(rightInnerStatCard as any)} />
          </div>
        )}

        {chart && chart.type === 'bar' && <AppBarChart {...chart} />}
        {chart && chart.type === 'area' && <AppAreaChart {...chart} />}
        {chart && chart.type === 'pie' && <AppPieChart {...chart} />}
      </CardContent>
    </Card>
  )
}
